<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { initializeAudioAlerts } from 'dashboard/helper/scriptHelpers';
import { useMapGetter } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import wootConstants from 'dashboard/constants/globals';

const { uiSettings, updateUISettings } = useUISettings();
const currentUser = useMapGetter('getCurrentUser');

const isSoundEnabled = computed(() => {
  const val = uiSettings.value?.enable_audio_alerts;
  return val && val !== 'none';
});

function toggleSound() {
  const newVal = isSoundEnabled.value ? 'none' : 'mine';
  updateUISettings({
    enable_audio_alerts: newVal,
    always_play_audio_alert: newVal !== 'none',
  });
  nextTick(() => {
    if (currentUser.value) {
      initializeAudioAlerts({
        ...currentUser.value,
        ui_settings: { ...uiSettings.value, enable_audio_alerts: newVal, always_play_audio_alert: newVal !== 'none' },
      });
    }
  });
}

const props = defineProps({
  pageTitle: { type: String, required: true },
  hasAppliedFilters: { type: Boolean, required: true },
  hasActiveFolders: { type: Boolean, required: true },
  activeStatus: { type: String, required: true },
  isOnExpandedLayout: { type: Boolean, required: true },
  activeTabCount: { type: Number, default: 0 },
  isListLoading: { type: Boolean, required: true },
  appliedFiltersCount: { type: Number, default: 0 },
});

const emit = defineEmits([
  'addFolders',
  'deleteFolders',
  'resetFilters',
  'basicFilterChange',
  'filtersModal',
  'search',
  'refreshList',
]);

const searchQuery = ref('');
const searchType = ref('contacts');
const showDropdown = ref(false);
const chipRef = ref(null);
const dropdownStyle = ref({});

const searchTypeLabel = computed(() =>
  searchType.value === 'contacts' ? 'Contatos' : 'Mensagens'
);

const placeholderText = computed(() =>
  searchType.value === 'contacts'
    ? 'Buscar por nome, telefone ou email...'
    : 'Buscar por conteúdo da mensagem...'
);

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return props.hasAppliedFilters || props.hasActiveFolders;
});

const onSearchInput = () => {
  emit('search', searchQuery.value, searchType.value);
};

const clearSearch = () => {
  searchQuery.value = '';
  emit('search', '', searchType.value);
};

useEmitter('clearSearchInput', () => {
  if (searchQuery.value) clearSearch();
});

function positionDropdown() {
  if (!chipRef.value) return;
  const rect = chipRef.value.getBoundingClientRect();
  const dark = document.body.classList.contains('dark');
  dropdownStyle.value = {
    position: 'fixed',
    top: `${rect.bottom + 6}px`,
    left: `${rect.right - 155}px`,
    width: '155px',
    background: dark ? '#1e293b' : '#ffffff',
    border: `1px solid ${dark ? 'rgba(255,255,255,0.12)' : '#e2e8f0'}`,
    borderRadius: '10px',
    boxShadow: dark
      ? '0 8px 24px rgba(0,0,0,0.5), 0 2px 6px rgba(0,0,0,0.3)'
      : '0 8px 24px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.06)',
    zIndex: '99999',
    padding: '4px',
  };
}

async function toggleDropdown() {
  showDropdown.value = !showDropdown.value;
  if (showDropdown.value) {
    await nextTick();
    positionDropdown();
  }
}

const selectType = (type) => {
  searchType.value = type;
  showDropdown.value = false;
  if (searchQuery.value.trim()) {
    emit('search', searchQuery.value, type);
  }
};

// ── Menu "..." com as acoes contextuais de pasta/filtro ──
const showActions = ref(false);
const actionsRef = ref(null);
const actionsStyle = ref({});

const actionItems = computed(() => {
  if (props.hasAppliedFilters && !props.hasActiveFolders) {
    return [
      { key: 'save', label: 'Salvar', icon: 'i-lucide-save', event: 'addFolders' },
      { key: 'reset', label: 'Limpar filtros', icon: 'i-lucide-x', event: 'resetFilters', danger: true },
    ];
  }
  if (props.hasActiveFolders) {
    return [
      { key: 'edit', label: 'Editar pasta', icon: 'i-lucide-pen-line', event: 'filtersModal' },
      { key: 'delete', label: 'Excluir pasta', icon: 'i-lucide-trash-2', event: 'deleteFolders', danger: true },
    ];
  }
  return [];
});

function positionActions() {
  if (!actionsRef.value) return;
  const rect = actionsRef.value.getBoundingClientRect();
  const dark = document.body.classList.contains('dark');
  actionsStyle.value = {
    position: 'fixed',
    top: `${rect.bottom + 6}px`,
    left: `${rect.right - 170}px`,
    width: '170px',
    background: dark ? '#1e293b' : '#ffffff',
    border: `1px solid ${dark ? 'rgba(255,255,255,0.12)' : '#e2e8f0'}`,
    borderRadius: '10px',
    boxShadow: dark
      ? '0 8px 24px rgba(0,0,0,0.5), 0 2px 6px rgba(0,0,0,0.3)'
      : '0 8px 24px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.06)',
    zIndex: '99999',
    padding: '4px',
  };
}

async function toggleActions() {
  showActions.value = !showActions.value;
  if (showActions.value) {
    await nextTick();
    positionActions();
  }
}

function runAction(item) {
  showActions.value = false;
  emit(item.event);
}

// Hover do menu tratado aqui porque o dropdown vai teleportado e usa style inline.
function onActionHover(event, isOver, danger) {
  const dark = document.body.classList.contains('dark');
  if (!isOver) {
    event.currentTarget.style.background = 'transparent';
    return;
  }
  if (danger) {
    event.currentTarget.style.background = dark ? 'rgba(239,68,68,0.12)' : '#fef2f2';
    return;
  }
  event.currentTarget.style.background = dark ? 'rgba(255,255,255,0.06)' : '#f8fafc';
}

function actionStyle(danger) {
  const dark = document.body.classList.contains('dark');
  return {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    padding: '7px 10px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '13px',
    color: danger ? '#ef4444' : (dark ? '#cbd5e1' : '#475569'),
    borderRadius: '7px',
    textAlign: 'left',
  };
}

const handleOutsideClick = (e) => {
  if (chipRef.value && chipRef.value.contains(e.target)) return;
  showDropdown.value = false;
};

const handleActionsOutsideClick = (e) => {
  if (actionsRef.value && actionsRef.value.contains(e.target)) return;
  showActions.value = false;
};

onMounted(() => {
  document.addEventListener('mousedown', handleOutsideClick);
  document.addEventListener('mousedown', handleActionsOutsideClick);
});
onUnmounted(() => {
  document.removeEventListener('mousedown', handleOutsideClick);
  document.removeEventListener('mousedown', handleActionsOutsideClick);
});

function optStyle(isActive) {
  const dark = document.body.classList.contains('dark');
  return {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    width: '100%',
    padding: '7px 10px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '13px',
    color: isActive
      ? (dark ? '#f1f5f9' : '#0f172a')
      : (dark ? '#94a3b8' : '#475569'),
    fontWeight: isActive ? '600' : '400',
    borderRadius: '7px',
    textAlign: 'left',
  };
}
</script>

<template>
  <div class="dc-header">
    <!-- Search row -->
    <div class="dc-search-row">
      <input
        v-model="searchQuery"
        type="text"
        class="dc-search-input"
        :placeholder="placeholderText"
        @input="onSearchInput"
      />
      <button v-if="searchQuery" class="dc-clear" @click="clearSearch">
        <span class="i-lucide-x" />
      </button>
      <div class="dc-chip-wrap">
        <button ref="chipRef" class="dc-chip" @click="toggleDropdown">
          <span class="i-lucide-search dc-chip-icon" />
          <span class="dc-chip-label">{{ searchTypeLabel }}</span>
          <span class="i-lucide-chevron-down dc-chip-chevron" />
        </button>
      </div>
    </div>

    <!-- Dropdown teleported to body -->
    <Teleport to="body">
      <Transition name="dd">
        <div
          v-if="showDropdown"
          :style="dropdownStyle"
          @mousedown.stop
        >
          <button
            :style="optStyle(searchType === 'contacts')"
            @mouseover="$event.target.style.background = document.body.classList.contains('dark') ? 'rgba(255,255,255,0.06)' : '#f8fafc'"
            @mouseout="$event.target.style.background = 'transparent'"
            @click="selectType('contacts')"
          >
            <span v-if="searchType === 'contacts'" class="i-lucide-check" style="font-size:14px;color:#6366f1;flex-shrink:0" />
            <span v-else style="width:14px;flex-shrink:0" />
            Contatos
          </button>
          <button
            :style="optStyle(searchType === 'messages')"
            @mouseover="$event.target.style.background = document.body.classList.contains('dark') ? 'rgba(255,255,255,0.06)' : '#f8fafc'"
            @mouseout="$event.target.style.background = 'transparent'"
            @click="selectType('messages')"
          >
            <span v-if="searchType === 'messages'" class="i-lucide-check" style="font-size:14px;color:#6366f1;flex-shrink:0" />
            <span v-else style="width:14px;flex-shrink:0" />
            Mensagens
          </button>
        </div>
      </Transition>
    </Teleport>

    <!-- Title row -->
    <div class="dc-title-row">
      <div class="dc-title-left">
        <h1 class="dc-title" :title="pageTitle">{{ pageTitle }}</h1>
        <button
          v-if="activeTabCount > 0 && !isListLoading"
          class="dc-badge"
          title="Recarregar conversas"
          @click="emit('refreshList')"
        >
          {{ activeTabCount > 999 ? '999+' : activeTabCount }}
          <span class="i-lucide-refresh-cw dc-badge-refresh" />
        </button>
      </div>
      <div class="dc-title-right">
        <!-- Pill de filtros: sempre visivel, com a contagem aplicada ao lado -->
        <div class="relative">
          <button class="dc-filter-pill" @click="emit('filtersModal')">
            <span class="dc-filter-pill-label">Filtros</span>
            <span class="i-lucide-filter" style="font-size:13px" />
            <template v-if="appliedFiltersCount > 0">
              <span class="dc-filter-pill-sep" />
              <span class="dc-filter-pill-count">
                {{ appliedFiltersCount }} aplicado{{ appliedFiltersCount > 1 ? 's' : '' }}
              </span>
            </template>
          </button>
          <div
            id="conversationFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>

        <!-- Menu "..." com as acoes contextuais de pasta/filtro -->
        <div class="relative">
          <button
            ref="actionsRef"
            class="dc-more-btn"
            :class="{ 'dc-more-btn--empty': actionItems.length === 0 }"
            title="Mais ações"
            @click="toggleActions"
          >
            <span class="i-lucide-more-horizontal" style="font-size:15px" />
          </button>
          <div
            id="saveFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
      </div>
    </div>

    <!-- Dropdown do menu "..." -->
    <Teleport to="body">
      <Transition name="dd">
        <div v-if="showActions" :style="actionsStyle" @mousedown.stop>
          <button
            :style="actionStyle(false)"
            @mouseover="onActionHover($event, true, false)"
            @mouseout="onActionHover($event, false, false)"
            @click="showActions = false; toggleSound()"
          >
            <span
              :class="isSoundEnabled ? 'i-lucide-volume-2' : 'i-lucide-volume-x'"
              style="font-size:14px;flex-shrink:0"
            />
            {{ isSoundEnabled ? 'Desativar som' : 'Ativar som' }}
          </button>
          <button
            v-for="item in actionItems"
            :key="item.key"
            :style="actionStyle(item.danger)"
            @mouseover="onActionHover($event, true, item.danger)"
            @mouseout="onActionHover($event, false, item.danger)"
            @click="runAction(item)"
          >
            <span :class="item.icon" style="font-size:14px;flex-shrink:0" />
            {{ item.label }}
          </button>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.dc-header {
  display: flex;
  flex-direction: column;
}

/* Cada faixa (busca / titulo) e fechada por uma divisoria de 1px. */
.dc-search-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  margin: 10px 0 0;
  height: 36px;
  border-bottom: 1px solid #e9eaee;
  padding-bottom: 8px;
}

.dark .dc-search-row {
  border-bottom-color: rgba(255, 255, 255, 0.1);
}

.dc-search-input {
  flex: 1 !important;
  min-width: 0 !important;
  height: 100% !important;
  border: none !important;
  border-radius: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  outline: none !important;
  font-size: 13px !important;
  padding: 0 4px 0 2px !important;
  margin: 0 !important;
  appearance: none !important;
  -webkit-appearance: none !important;
}

.dc-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px; height: 18px;
  border-radius: 4px;
  border: none;
  font-size: 11px;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.1s;
}

.dc-chip-wrap {
  position: relative;
  flex-shrink: 0;
}

.dc-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 30px;
  padding: 0 10px;
  border-radius: 15px;
  border: 1.5px solid #d1d5db;
  background: #ffffff;
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 500;
  color: #475569;
  transition: all 0.15s;
  white-space: nowrap;
}
.dc-chip:hover {
  border-color: #9ca3af;
  background: #f8fafc;
  color: #334155;
}

.dc-chip-icon { font-size: 13px; opacity: 0.6; }
.dc-chip-label { line-height: 1; }
.dc-chip-chevron { font-size: 10px; opacity: 0.4; margin-left: -1px; }

/* Titulo e chips formam um bloco so: sem divisoria entre eles. */
.dc-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
}

.dc-title-left { display: flex; align-items: center; gap: 8px; min-width: 0; }

.dc-title {
  font-size: 14px;
  font-weight: 700;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Badge clicavel: recarrega a lista do filtro ativo. */
.dc-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-width: 20px; height: 20px;
  padding: 0 7px;
  border: none;
  border-radius: 10px;
  background: #6366f1;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
  cursor: pointer;
  transition: background 0.15s;
}
.dc-badge:hover { background: #4f52d9; }
.dc-badge:active { background: #4548c4; }

.dc-badge-refresh {
  font-size: 10px;
  flex-shrink: 0;
  transition: transform 0.4s ease;
}
.dc-badge:hover .dc-badge-refresh { transform: rotate(180deg); }
.dc-badge:active .dc-badge-refresh { transform: rotate(360deg); }

.dc-title-right { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }

/* Pill de filtros: fundo cinza do sistema, sem borda. */
.dc-filter-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 8px;
  border: none;
  background: #f2f3f6;
  color: #5f6b81;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
  white-space: nowrap;
}
.dc-filter-pill:hover { background: #e8eaef; }
.dc-filter-pill-label { line-height: 1; }
.dc-filter-pill-sep {
  width: 1px;
  height: 13px;
  background: #ccd0da;
  flex-shrink: 0;
}
.dc-filter-pill-count { color: #5566e8; line-height: 1; }

.dc-more-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 26px;
  border: none;
  border-radius: 8px;
  background: #f2f3f6;
  color: #5f6b81;
  cursor: pointer;
  transition: background 0.15s;
  flex-shrink: 0;
}
.dc-more-btn:hover { background: #e8eaef; }

.dc-sound-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  color: #22c55e;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
}
.dc-sound-btn:hover { background: #f0fdf4; border-color: #86efac; }
.dc-sound-btn--off { color: #94a3b8; }
.dc-sound-btn--off:hover { background: #f8fafc; border-color: #cbd5e1; }
</style>

<style>
.dark .dc-search-input { color: #e2e8f0 !important; }
.dark .dc-search-input::placeholder { color: #64748b !important; }
.dark .dc-clear { background: rgba(255,255,255,0.1); color: #94a3b8; }
.dark .dc-clear:hover { background: rgba(255,255,255,0.18); color: #cbd5e1; }
.dark .dc-chip { background: rgba(255,255,255,0.06) !important; border-color: rgba(255,255,255,0.12) !important; color: #94a3b8 !important; }
.dark .dc-chip:hover { background: rgba(255,255,255,0.1) !important; border-color: rgba(255,255,255,0.2) !important; color: #e2e8f0 !important; }
.dark .dc-title { color: #f1f5f9 !important; }
.dark .dc-filter-pill,
.dark .dc-more-btn { background: rgba(255,255,255,0.06) !important; color: #94a3b8 !important; }
.dark .dc-filter-pill:hover,
.dark .dc-more-btn:hover { background: rgba(255,255,255,0.1) !important; }
.dark .dc-filter-pill-sep { background: rgba(255,255,255,0.18) !important; }
.dark .dc-filter-pill-count { color: #818cf8 !important; }
.dd-enter-active, .dd-leave-active { transition: opacity 0.12s, transform 0.12s; }
.dd-enter-from, .dd-leave-to { opacity: 0; transform: translateY(-4px); }
</style>
